import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import downloadjs from 'downloadjs';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { CircularProgress, Divider, Grid, Stack } from '@mui/material';
import { ApiContractRequest, ApiCustomField, ApiItemRef, ApiItemTypes, type ApiContract, type ApiContractType, type ApiDocument } from '@agunity/api-v4';

import AgDropdown from '../../stories/AgDropdown';
import AgLoadingBackdrop from '../AgLoadingBackdrop';
import { AgColor } from '../../stories/Styles/AgColor';
import AgButton from '../../stories/Components/AgButton';
import AgTypography from '../../stories/Styles/AgTypography';
import AgTextArea from '../../stories/Components/AgTextArea';
import AgDatePicker from '../../stories/Components/CustomDropdowns/AgDatePicker';

import { useAppDispatch } from '../../redux/hooks';
import { useToast } from '../../redux/hooks/toast';
import ContractTableFiles from './ContractTableFiles';
import { deleteDocument, downloadDocument, getContractItem, patchContractItems, postContractItems, uploadFile } from './redux';
import type { DropdownOptions, Errors, Touch } from './types';
import * as S from './styled';
import CustomFields from './CustomFields';

type ContentModal = {
  canEdit?: boolean;
  itemRef: ApiItemRef[],
  contractId?: number;
  isAdd?: boolean;
  options: Record<string, DropdownOptions[]>;
  tagList: ApiCustomField[];
  closeModal: () => void;
  callback: () => void;
}

type Docs = { file?: File } & ApiDocument;

const initialDetails = {
  contractType: undefined,
  startDate: undefined,
  endDate: undefined,
  description: undefined,
  data: [],
};

const format = 'YYYY-MM-DD';
const maxSize = 5 * 1024 * 1024; // 5 MB in bytes

const ContentModal = ({ canEdit, itemRef, contractId, isAdd, options, tagList, closeModal, callback, }: ContentModal) => {
  const messages = useIntl().formatMessage;
  const { toast } = useToast();
  const fileUploadRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();

  const [details, setDetails] = useState<ApiContract>(initialDetails);
  const [docs, setDocs] = useState<Docs[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [hasCustomFieldErrors, setHasCustomFieldErrors] = useState(false);
  const [touch, setTouch] = useState<Touch>({});
  const [deleteDoc, setDeleteDoc] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const { isExpiryMandatory, itemType } = useMemo(() => {
    return options?.contractTypes.find(({ id }) => id === details?.contractType) as ApiContractType;
  }, [details?.contractType, options?.contractTypes]) || {};

  // Contract item details
  const getContract = useCallback(async () => {
    if(!contractId) return;
    setIsLoading(true);
    try {
      const { data } = await dispatch( getContractItem(contractId!));
      setDetails(data);
      setDocs(data.documents || []);
    } catch(error){
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [contractId, dispatch]);

  // Validate necessary fields
  const validateFields = useCallback((data: ApiContract) => {
    const errorMessages = Object.entries({ ...initialDetails, ...data }).reduce((acc, [key, keyValue]) => {
      const value = keyValue ?? '';
      let message = '';
      
      // Required keys validation
      const requiredKeys: string[] = ['contractType', 'targets', 'values', isExpiryMandatory ? 'endDate' : ''];
      if (requiredKeys.includes(key)) {
        if (!value?.toString()) message = messages({ id: 'CONTRACTS_CANNOT_BE_EMPTY' });
      }
      return { ...acc, [key]: message };
    }, {});

    if (Object.keys(errorMessages).length) setErrors(errorMessages);
  }, [isExpiryMandatory, messages]);

  const handleChange = useCallback(async (event: any, key: string) => {
    let value = event?.target?.value ?? event ?? '';

    if (key === 'contractType') {
      value = options.contractTypes.find(({ name }) => name === value)?.id;
      const data = (details?.data || [])
        .map((item) => ({ ...item, targetId: tagList.find((tag) => tag.key === item.key)?.targetId }))
        .filter((item: any) => item.targetId ? item.targetId === value : true);
      setDetails((prev: any) => ({ ...prev, contractType: value, data }));
      return;
    }

    if (key === 'customFields') {
      const tagListMap = new Map(tagList.map((tag) => [tag.key, tag]));
      const data = value.map(({ key, value }: any) => ({ ...tagListMap.get(key), value }));
      setDetails((prev: any) => ({ ...prev, data }));
      return;
    }

    setDetails((prev: any) => ({ ...prev, [key]: value }));
  }, [details?.data, options.contractTypes, tagList]);

  const handleBlur = (key: string) => {
    setTouch((prev) => ({ ...prev, [key]: true }));
  };

  const handleChooseFile = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click();
      fileUploadRef.current.value = '';
    }
  };

  const handleSelectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files || [];

    const files = Array.from(fileList).map((file) => ({
      name: file.name,
      documentType: file.type,
      documentSize: file.size,
      file
    }));

    // has duplicate file(s)
    const duplicateFiles = files.filter(({ name }) => {
      return docs.some(({ name: documentName }) => name === documentName);
    });

    const removedDuplicate = files.filter(file => {
      return !docs.some(({ name: documentName }) => file.name === documentName);
    });

    if (duplicateFiles.length) {
      toast('neutral', messages({ id: 'CONTRACTS_FILE_DUPLICATE' }, { fileName: duplicateFiles?.[0]?.name || '' }), { temporary: true });
    }

    setDocs((prevFiles) => [...(prevFiles || []), ...removedDuplicate]);
  };

  const handleDeleteFile = ({ name, documentKey }: ApiDocument) => {
    setDocs((prev) => prev.filter(({ name: documentName }) => name !== documentName));
    documentKey && setDeleteDoc((prev) => ([documentKey, ...prev]));
  };

  const handleDownload = useCallback(async (row: Docs) => {
    try {
      let file: File | Blob | undefined;
      if (row.file) file = row.file;
      else {
        const response = await dispatch(downloadDocument(row.documentKey!));
        file = response.data;
      }

      const fileName = row.name || row.file?.name;

      downloadjs(file, fileName);
    } catch (error: any) {
      console.error(error);
    }
  },
    [dispatch]
  );

  const handleSave = useCallback(async () => {
    try {
      let payload: ApiContractRequest = {
        contractType: details?.contractType,
        startDate: details?.startDate || undefined,
        endDate: details?.endDate || undefined,
        description: details?.description,
        data: (details?.data || []).filter((item: any) => item.targetId ? item.targetId === details?.contractType : true),
      };

      // Set all touch to true
      const touchAll = Object.keys(payload).reduce((acc, key) => ({ ...acc, [key]: true }), {});
      setTouch(touchAll);

      // Check if there's an errors
      const hasErrors = Object.values(errors).filter(Boolean).length;
      if (hasErrors) {
        toast('error', messages({ id: 'CONTRACTS_ERROR_MESSAGE' }), { temporary: true });
        return;
      };

      if (hasCustomFieldErrors) {
        toast('error', messages({ id: 'CONTRACTS_CUSTOM_FIELD_ERROR_MESSAGE' }), { temporary: true });
        return;
      }

      // File(s) is required
      if (docs.length === 0) {
        toast('error', messages({ id: 'CONTRACTS_NO_DOCS_ERROR_MESSAGE' }), { temporary: true });
        return;
      }

      // 50 files are allowed
      if (docs.length > 50) {
        toast('error', messages({ id: 'CONTRACTS_FILE_COUNT_ERROR_MESSAGE' }), { temporary: true });
        return;
      }

      // only 5 MB are allowed
      const isMoreThan5MB = docs.some(({ documentSize }) => (documentSize || 0) > maxSize);
      if (isMoreThan5MB) {
        toast('error', messages({ id: 'CONTRACTS_FILE_SIZE_ERROR_MESSAGE' }), { temporary: true });
        return;
      }

      let referenceId: number = contractId!;
      const { itemId } = itemRef.find((item) => item.itemType === itemType) || {};

      setIsSaveLoading(true);
      if (isAdd) {
        // Post contract items
        const { data: contractId } = await dispatch(postContractItems(itemType!, itemId!, payload));
        referenceId = contractId;
        toast('success', messages({ id: 'CONTRACTS_ADD_SUCCESS' }), { temporary: true });
        closeModal();
      } else {
        // Update contract items
        await dispatch(patchContractItems(referenceId, payload));
        toast('success', messages({ id: 'CONTRACTS_UPDATE_SUCCESS' }), { temporary: true });
      }

      const localFiles = docs.filter((doc) => !!doc.file);

      // Wait for all uploads to finish
      await Promise.all(
        localFiles.map((doc) =>
          dispatch(
            uploadFile({
              file: doc.file!,
              itemId: referenceId,
              itemType: ApiItemTypes.Contract as number,
              name: doc.name!,
            })
          )
        )
      );

      if (deleteDoc.length) {
        // Wait for some docs to be deleted
        await Promise.all(deleteDoc.map((documentKey) => dispatch(deleteDocument(documentKey))));
      }

      if(!isAdd) await getContract();
      callback && callback();
    }
    catch (error: any) {
      console.error(error);
    } finally {
      setIsSaveLoading(false);
    }
  }, [details?.contractType, details?.startDate, details?.endDate, details?.description, details?.data, errors, hasCustomFieldErrors, docs, contractId, itemRef, isAdd, deleteDoc, getContract, callback, toast, messages, itemType, dispatch, closeModal]);

  useEffect(() => {
    const debounce = setTimeout(getContract);
    return () => clearTimeout(debounce);
  }, [getContract]);

  useEffect(() => {
    validateFields(details);
  }, [details, validateFields]);

  return (
    <>
      {isLoading ? (
        <AgLoadingBackdrop />
      ) : null}
      <Grid container spacing={2} sx={{ '.MuiFormControl-root': { marginRight: 0 }, maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}>
        <Grid item xs={12}>
          <AgDropdown
            id='contractType'
            inputLabel={messages({ id: 'CONTRACTS_ADD_FORM_TYPE_LABEL' })}
            placeholder={messages({ id: 'SELECT' })}
            value={(options?.contractTypes || [])?.find(({ id }) => id === details?.contractType)?.name || ''}
            options={options?.contractTypes || []}
            onChange={(event: number) => handleChange(event, 'contractType')}
            onBlur={() => handleBlur('contractType')}
            error={(!!errors?.contractType && touch?.contractType)}
            caption={(!!errors?.contractType && touch?.contractType) ? `${messages({ id: 'CONTRACTS_ADD_FORM_TYPE_LABEL' })} ${errors?.contractType}` : ''}
          />
        </Grid>
  
        <Grid item xs={12} md={6}>
          <AgDatePicker
            id='startDate'
            maxDate={dayjs(details?.endDate, format)}
            optionalText
            label={messages({ id: 'CONTRACTS_ADD_FORM_START_DATE_LABEL' })}
            value={details.startDate ? dayjs(dayjs(details.startDate, format)) : undefined}
            onChange={(value) => handleChange(value ? dayjs(value).format(format) : '', 'startDate')}
          />
        </Grid>
  
        <Grid item xs={12} md={6}>
          <AgDatePicker
            id='endDate'
            minDate={dayjs(details?.startDate, format)}
            optionalText={!isExpiryMandatory}
            label={messages({ id: 'CONTRACTS_ADD_FORM_END_DATE_LABEL' })}
            value={details.endDate ? dayjs(dayjs(details.endDate, format)) : undefined}
            onChange={(value) => handleChange(value ? dayjs(value).format(format) : '', 'endDate')}
            onOpen={() => handleBlur('endDate')}
            error={(!!errors?.endDate && touch?.endDate)}
            caption={(!!errors?.endDate && touch?.endDate) ? `${messages({ id: 'CONTRACTS_ADD_FORM_END_DATE_LABEL' })} ${errors?.endDate}` : ''}
          />
        </Grid>
  
        <Grid item xs={12}>
          <AgTextArea
            optionalText
            label={messages({ id: 'CONTRACTS_ADD_FORM_DESCRIPTION_LABEL' })}
            placeholder={messages({ id: 'CONTRACTS_ADD_FORM_DESCRIPTION_PLACEHOLDER' }, { length: 300 })}
            value={details?.description || ''}
            onChange={(event) => handleChange(event, 'description')}
            style={{ minHeight: '64px', resize: 'vertical' }}
            onBlur={() => handleBlur('description')}
            error={(!!errors?.description && touch?.description)}
            caption={(!!errors?.description && touch?.description) ? `${messages({ id: 'CONTRACTS_ADD_FORM_DESCRIPTION_LABEL' })} ${errors?.description}` : ''}
          />
        </Grid>
  
        {/* Documents */}
  
        <Grid item xs={12}>
          <Stack direction='row' display='flex' alignItems='center' justifyContent='space-between'>
            <AgTypography contentStyle='AP.H4.Medium' style={{ color: AgColor.grey8 }}>
              {messages({ id: 'CONTRACTS_ADD_FORM_FILE_LABEL' })}
            </AgTypography>
          </Stack>
        </Grid>
  
        <Grid item xs={12}>
          <AgTypography contentStyle='AP.H4.Medium' style={{ color: AgColor.grey6 }}>
            {messages({ id: 'CONTRACTS_ADD_FORM_FILE_DESC' })}
          </AgTypography>
        </Grid>
  
        <Grid item xs={12}>
          <Stack direction='row' gap={2} justifyContent='space-between' alignItems='flex-end'>
            <AgButton
              variant='outlined'
              label={messages({ id: 'CONTRACTS_ADD_FORM_FILE_BTN' })}
              onClick={handleChooseFile}
              disabled={!canEdit}
            />
            {docs.length > 0 ? (
              <AgTypography contentStyle='AP.Assistive.Text.Regular' style={{ color: docs.length > 50 ? AgColor.errorMain : AgColor.grey6 }}>
                {messages({ id: 'CONTRACTS_FILE_COUNT' }, { count: docs.length })}
              </AgTypography>
            ) : null}
          </Stack>
          <input
            ref={fileUploadRef}
            type='file'
            accept='.pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.odt,.ods'
            multiple
            style={{ display: 'none' }}
            onChange={handleSelectFile}
          />
        </Grid>
  
        <Grid item xs={12}>
          {docs?.length !== 0 ? (
            <S.TableContainer>
              <ContractTableFiles
                canEdit={canEdit}
                maxSize={maxSize}
                documents={docs || []}
                onView={handleDownload}
                onDelete={handleDeleteFile}
              />
            </S.TableContainer>
          ) : null}
        </Grid>

        {/* Custom Fields */}
        <Grid item xs={12}>
          <CustomFields
            tagList={tagList}
            handleChange={(data) => handleChange(data, 'customFields')}
            targetId={details?.contractType}
            details={details?.data || []}
            setHasCustomFieldErrors={setHasCustomFieldErrors}
            showError={hasCustomFieldErrors && touch?.data}
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Divider sx={{ margin: '16px 0', color: AgColor.grey4 }} />
        </Grid>
  
        <Grid item xs={12}>
          <Stack direction='row' gap={2} justifyContent='flex-end'>
            <AgButton variant='outlined' label={'CANCEL'} onClick={closeModal} />
            {canEdit ? (
              <AgButton 
                endIcon={isSaveLoading ? <CircularProgress color='inherit' size={18} /> : null} 
                disabled={isSaveLoading} 
                label={messages({ id: isAdd ? 'CONTRACTS_ADD_BTN' : 'SAVE' })} 
                onClick={handleSave} 
              />
            ) : null}
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};

export default ContentModal;